<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { createPopper } from '@popperjs/core';
import Btn from '../Btn/Btn.vue';

defineProps<{ buttonLabel?: string }>();
const emit = defineEmits(['applyFilters', 'closeFiltersDropdown', 'openFiltersDropdown']);

const popper = ref();
const toggleDropdownButton = ref<HTMLButtonElement | null>(null);
const filtersDropdown = ref<HTMLDivElement | null>(null);
const isDropdownOpen = ref(false);

onMounted(() => {
	popper.value = createPopper(toggleDropdownButton.value, filtersDropdown.value, {
		placement: 'bottom',
		modifiers: [
			{
				name: 'offset',
				options: {
					offset: [0, 8],
				},
			},
		],
	});
});

function toggleDropdown() {
	if (isDropdownOpen.value) {
		hideDropdown();
		return;
	}
	// popper uses data-show attribute
	filtersDropdown.value.setAttribute('data-show', '');
	popper.value.update(); // Get correct position when showing on screen
	isDropdownOpen.value = true;
	// Notify parent to handle the state of slotted filters
	emit('openFiltersDropdown');
}

function hideDropdown() {
	filtersDropdown.value.removeAttribute('data-show');
	isDropdownOpen.value = false;
	// Notify parent to handle the state of slotted filters
	emit('closeFiltersDropdown');
}
</script>
<template>
	<div data-test="custom-filters" v-click-outside="hideDropdown" @keydown.esc="hideDropdown">
		<button
			ref="toggleDropdownButton"
			data-test="toggle-dropdown-button"
			name="Show filters button"
			title="Show filters"
			aria-controls="filtersDropdown"
			:aria-expanded="isDropdownOpen"
			aria-label="Show filters button"
			class="show-filters-button icon-button"
			@mousedown="toggleDropdown"
			@keydown.enter="toggleDropdown"
		>
			<span class="bx bx-filter" />
		</button>
		<div
			ref="filtersDropdown"
			id="filtersDropdown"
			data-test="filters-dropdown"
			class="filters-dropdown bg-white rounded-2 shadow-dropdown"
		>
			<slot name="filters"></slot>
			<Btn
				:label="buttonLabel ?? 'Apply filters'"
				variant="secondary"
				size="small"
				class="m-2"
				@click.stop="$emit('applyFilters')"
			/>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.filters-dropdown {
	display: none;
	z-index: $z-index-3;
}

.filters-dropdown[data-show] {
	display: flex;
	flex-direction: column;
}

.show-filters-button:has(+ .filters-dropdown[data-show]) {
	background-color: $color-gray-300;
	color: $color-gray-50;
}
</style>
